import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { UploadCloud, FileText, Trash2, Image as ImageIcon, PenLine, Check, X } from "lucide-react";
import { toast } from "sonner";
import {
  SourcePreviewDialog, SourceStatusBadge, validateSourceFile,
  isImage, sourceLabel, SUPPORTED_SOURCE_TEXT, SOURCE_ACCEPT,
} from "@/components/SourceFiles";

export default function Uploads() {
  const { activeWorkspace, canEdit } = useAuth();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [label, setLabel] = useState("");
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const load = () => api.get(`/workspaces/${activeWorkspace.id}/uploads`).then(({ data }) => setFiles(data || []));
  useEffect(() => { if (activeWorkspace) load(); /* eslint-disable-next-line */ }, [activeWorkspace]);

  const upload = async (fs) => {
    setBusy(true);
    for (const f of fs) {
      const err = validateSourceFile(f);
      if (err) { toast.error(err); continue; }
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post(`/workspaces/${activeWorkspace.id}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data.duplicate) toast.warning(data.duplicate_message);
        else if (data.extraction_status === "unreadable") toast.warning(`${f.name}: ${data.extraction_note}`);
        else if (data.extraction_status === "ready") toast.success(`${f.name} read successfully.`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Could not upload ${f.name}`);
      }
    }
    setBusy(false); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this file? Documents already generated from it stay unchanged.")) return;
    await api.delete(`/workspaces/${activeWorkspace.id}/uploads/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
  };

  const saveLabel = async (id) => {
    try {
      await api.patch(`/workspaces/${activeWorkspace.id}/uploads/${id}`, { display_label: label });
      setRenaming(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not rename file"); }
  };

  return (
    <div className="max-w-6xl mx-auto p-8" data-testid="uploads-page">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-white">Source files</h1>
        <p className="text-sm text-slate-400">Reusable source material. Fexidoc reads text from PDF, Word, text and CSV files. Click any file to preview it.</p>
      </div>
      {canEdit && (
        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); upload(Array.from(e.dataTransfer.files)); }}
          className={`block border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition ${drag ? "border-cyan-400 bg-cyan-500/5" : "border-[#1E293B] hover:border-slate-600"}`}
          data-testid="uploads-drop">
          <UploadCloud size={22} className="mx-auto text-cyan-300" />
          <div className="mt-2 text-sm text-slate-200">{busy ? "Uploading and reading files…" : "Click to upload or drop files here"}</div>
          <div className="text-[11px] text-slate-500 mt-1">{SUPPORTED_SOURCE_TEXT}</div>
          <div className="text-[11px] text-slate-600 mt-1">Scanned or image-only PDFs can't be read yet — we'll tell you if that happens.</div>
          <input ref={inputRef} type="file" multiple accept={SOURCE_ACCEPT} onChange={(e) => upload(Array.from(e.target.files))} className="hidden" data-testid="uploads-input" />
        </label>
      )}
      <div className="mt-6 fx-panel overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0F1F] text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Type</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Size</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Uploaded</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id} className="border-t border-[#1E293B] hover:bg-white/5 transition" data-testid={`upload-row-${f.id}`}>
                <td className="px-4 py-3">
                  {renaming === f.id ? (
                    <div className="flex items-center gap-2">
                      <input value={label} onChange={(e) => setLabel(e.target.value)} className="bg-[#0D1224] border border-[#1E293B] rounded px-2 py-1 text-xs text-slate-100 focus:border-cyan-400 outline-none" data-testid={`upload-rename-input-${f.id}`} />
                      <button onClick={() => saveLabel(f.id)} className="text-emerald-400" data-testid={`upload-rename-save-${f.id}`}><Check size={14} /></button>
                      <button onClick={() => setRenaming(null)} className="text-slate-500"><X size={14} /></button>
                    </div>
                  ) : (
                    <button onClick={() => setSelected(f)} className="flex items-center gap-2 text-left" data-testid={`upload-open-${f.id}`}>
                      {isImage(f) ? <ImageIcon size={14} className="text-violet-300" /> : <FileText size={14} className="text-cyan-300" />}
                      <span className="text-slate-100">{sourceLabel(f)}</span>
                    </button>
                  )}
                  {f.extraction_note && <div className="text-[11px] text-amber-300/80 mt-1 max-w-md">{f.extraction_note}</div>}
                </td>
                <td className="px-4 py-3"><SourceStatusBadge status={f.extraction_status} testid={`upload-status-${f.id}`} /></td>
                <td className="px-4 py-3 text-slate-400 hidden md:table-cell uppercase text-xs mono">{f.file_type}</td>
                <td className="px-4 py-3 text-slate-400 hidden md:table-cell mono text-xs">{(f.size / 1024).toFixed(1)} KB</td>
                <td className="px-4 py-3 text-slate-500 text-xs hidden lg:table-cell">{new Date(f.created_at).toLocaleString()}</td>
                <td className="px-3">
                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setRenaming(f.id); setLabel(sourceLabel(f)); }} className="text-slate-500 hover:text-cyan-300" title="Rename label" data-testid={`upload-rename-${f.id}`}><PenLine size={14} /></button>
                      <button onClick={() => del(f.id)} className="text-slate-500 hover:text-red-400" title="Delete" data-testid={`upload-delete-${f.id}`}><Trash2 size={14} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {files.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-sm text-slate-500">No source files uploaded yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && <SourcePreviewDialog file={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
